import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import { usePageMeta } from '../lib/page-meta';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';

export default function WorkspaceSelectionPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const {
    isAuthenticated,
    pendingWorkspaceSelection,
    hasPendingWorkspaceSelection,
    selectWorkspace,
    clearPendingWorkspaceSelection,
    isLoading,
  } = useAuth();
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  usePageMeta({
    title: t('workspaceSelection.metaTitle'),
    description: t('workspaceSelection.metaDescription'),
    canonicalPath: '/select-workspace',
    robots: 'noindex,nofollow',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!hasPendingWorkspaceSelection) {
      navigate('/login', { replace: true });
      return;
    }

    setSelectedOrganisationId((current) => (
      current || pendingWorkspaceSelection?.workspaces[0]?.organisation.id || ''
    ));
  }, [hasPendingWorkspaceSelection, isAuthenticated, navigate, pendingWorkspaceSelection?.workspaces]);

  if (!pendingWorkspaceSelection) {
    return null;
  }

  const handleContinue = async () => {
    if (!selectedOrganisationId) {
      setFormError(t('workspaceSelection.errors.required'));
      return;
    }

    setFormError(null);
    try {
      const result = await selectWorkspace(selectedOrganisationId);
      navigate(result.status === 'authenticated' ? pendingWorkspaceSelection.nextPath : '/verify-email', {
        replace: true,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
      } else {
        setFormError(t('workspaceSelection.errors.unexpected'));
      }
    }
  };

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div className="acc-auth-panel__header">
            <div className="acc-auth-panel__eyebrow">{t('workspaceSelection.eyebrow')}</div>
            <h1 className="acc-auth-card__title">{t('workspaceSelection.title')}</h1>
            <p className="acc-auth-card__description">
              {t('workspaceSelection.description', { email: pendingWorkspaceSelection.user.email })}
            </p>
          </div>

          {formError && (
            <div role="alert" className="acc-auth-banner acc-auth-banner--danger">
              {formError}
            </div>
          )}

          <div className="acc-auth-form" style={{ gap: 18 }}>
            <div className="acc-auth-divider">{t('workspaceSelection.listLabel')}</div>
            <div className="acc-auth-workspace-selector">
              {pendingWorkspaceSelection.workspaces.map((workspace) => {
                const isSelected = workspace.organisation.id === selectedOrganisationId;
                return (
                  <button
                    key={workspace.organisation.id}
                    type="button"
                    className={`acc-auth-workspace-option${isSelected ? ' acc-auth-workspace-option--selected' : ''}`}
                    onClick={() => {
                      setSelectedOrganisationId(workspace.organisation.id);
                      setFormError(null);
                    }}
                  >
                    <span className="acc-auth-workspace-option__copy">
                      <span
                        className="acc-auth-workspace-option__title"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                      >
                        <span>{workspace.organisation.name}</span>
                        {workspace.organisation.isDemoWorkspace && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '3px 8px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#8a5a00',
                              background: 'rgba(245, 158, 11, 0.14)',
                              border: '1px solid rgba(245, 158, 11, 0.26)',
                            }}
                          >
                            Demo workspace
                          </span>
                        )}
                      </span>
                      <span className="acc-auth-workspace-option__meta">{workspace.organisation.slug}</span>
                    </span>
                    <span className="acc-auth-workspace-option__role">{t(`common:roles.${workspace.role}`)}</span>
                  </button>
                );
              })}
            </div>

            <div className="acc-auth-form__actions">
              <Button
                type="button"
                size="lg"
                disabled={isLoading || !selectedOrganisationId}
                onClick={handleContinue}
                style={{ width: '100%' }}
              >
                {isLoading ? t('workspaceSelection.submitting') : t('workspaceSelection.submit')}
              </Button>
            </div>

            <div className="acc-auth-secondary-actions" data-testid="workspace-selection-secondary-actions">
              <button
                type="button"
                className="acc-link-inline acc-auth-secondary-actions__button"
                onClick={() => {
                  clearPendingWorkspaceSelection();
                  navigate('/login', { replace: true });
                }}
              >
                {t('workspaceSelection.useDifferentAccount')}
              </button>
              <Link to="/login" className="acc-link-inline acc-auth-secondary-actions__link">
                {t('workspaceSelection.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
